import { useCallback, useEffect, useState } from "react";
import { listBdcBranches } from "../../../services";
import { keycloak } from "../../../services/keycloak";
import { BdcBranch } from "../../../types/bdc.type";
import { rowsOf } from "../format";
import {
  ALL_BRANCHES,
  companyIdFilter,
  readStoredBranch,
  sortBranches,
  viewedBranches,
  writeStoredBranch,
} from "./branches";

/** The BDC branches this viewer may see (GET /bdc/branches), code-sorted. */
export function useBdcBranches() {
  const [branches, setBranches] = useState<BdcBranch[]>([]);
  const [loading, setLoading] = useState(false);
  /** True once the list answered (or failed) — screens wait for it before narrowing by branch. */
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcBranches();
      setBranches(sortBranches(rowsOf(data)));
    } catch {
      // interceptor reported it; no branches → no selector, no labels' colors
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { branches, loading, ready, reload, setBranches };
}

/**
 * Which branch the viewer looks at, remembered per Keycloak user. `companyId` is what to send
 * (undefined = ทุกสาขา, also when the remembered branch is no longer visible — never a 403).
 */
export function useBdcBranchSelection(branches: BdcBranch[]) {
  const viewerId = keycloak.subject;
  const [selected, setSelected] = useState<string>(() => readStoredBranch(viewerId));

  const select = useCallback(
    (value: string) => {
      setSelected(value);
      writeStoredBranch(viewerId, value);
    },
    [viewerId],
  );

  const companyId = companyIdFilter(branches, selected);
  return {
    selected: companyId ?? ALL_BRANCHES,
    companyId,
    viewed: viewedBranches(branches, selected),
    select,
  };
}
